import { memo, useCallback, type FC } from "react";
import { useUnistyles } from "react-native-unistyles";
import { View } from "@src/components/View";
import { Text } from "@src/components/Text";
import { Button } from "@src/components/Button";
import { useAuth0Upsell } from "@src/hooks/useAuth0Upsell";
import { useAuth0Login } from "@src/hooks/useAuth0Login";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import { Icon } from "../Icon";

interface Auth0UpsellBannerProps {
  /** Custom styles to apply to the banner container */
  style?: object;
}

/**
 * Soft upsell banner component that prompts users to upgrade to Auth0.
 * Displayed below the search bar on the camera list screen.
 * Content is controlled via Amplitude feature flag.
 */
export const Auth0UpsellBanner: FC<Auth0UpsellBannerProps> = memo(({ style }) => {
  const { theme } = useUnistyles();
  const { config, showSoftBanner } = useAuth0Upsell();
  const { initiateAuth, isReady } = useAuth0Login();
  const { track } = useAmplitude();

  const handleUpgradePress = useCallback(async () => {
    track(ANALYTICS_EVENTS.AUTH0_UPSELL_BANNER_CLICKED, {
      mode: "soft",
      title: config.title,
    });

    try {
      // Directly initiate Auth0 login flow - same as clicking "Sign In" button
      // This will open the Auth0 browser/modal and handle token exchange automatically
      await initiateAuth();
    } catch (error) {
      console.error("[Auth0UpsellBanner] Auth initiation failed:", error);
    }
  }, [track, config.title, initiateAuth]);

  if (!showSoftBanner) {
    return null;
  }

  return (
    <View
      style={[
        {
          backgroundColor: theme.colors.surfaceCardInner,
          padding: theme.spacing.lg,
          borderRadius: theme.spacing.md,
          borderColor: theme.colors.interactive.buttons.default_background,
          borderWidth: 1,
        },
        style,
      ]}
    >
      <View flexDirection="row" gap="md" alignItems="flex-start">
        <View
          style={{
            backgroundColor: theme.colors.interactive.buttons.default_background,
            borderRadius: theme.spacing.sm,
            padding: theme.spacing.sm,
          }}
        >
          <Icon name="checkmark" size={20} color={theme.colors.white.default} />
        </View>
        <View flex={1} gap="xs">
          <Text textStyle="sm/semibold" style={{ color: theme.colors.text }}>
            {config.title}
          </Text>
          <Text textStyle="xs/regular" style={{ color: theme.colors.textMuted }}>
            {config.message}
          </Text>
          <View mt="sm">
            <Button
              onPress={handleUpgradePress}
              label={config.ctaText}
              size="sm"
              variant="primary"
              textStyle="xs/medium"
              disabled={!isReady}
              style={{
                alignSelf: "flex-start",
                paddingHorizontal: theme.spacing.xl,
                paddingVertical: theme.spacing.sm,
                minWidth: 0,
              }}
            />
          </View>
        </View>
      </View>
    </View>
  );
});

Auth0UpsellBanner.displayName = "Auth0UpsellBanner";
